import {
  UserActivityProvider,
  NotificationProvider,
  useContentShareState,
} from "amazon-chime-sdk-component-library-react";
import { VideoUIProvider } from "@platform/features/videoUI/VideoUIContext";
import { VideoUIFrameContainer } from "@platform/features/videoUI/components/VideoUIFrame/VideoUIFrameContainer";
import { useMeetingEndRedirect } from "../hooks/useMeetingEndRedirect";
import { DataMessagesProvider } from "../providers/DataMessagesProvider";
import MeetingStatusNotifier from "../containers/MeetingStatusNotifier";
import { useCustomMeetingManager } from "../hooks/useCustomMeetingManager";
import { useDeviceRefresh } from "../hooks/useDeviceRefresh";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DevicePermissionInitializer } from "../components/DevicePermissionInitializer";
import { useAppState } from "../providers/AppStateProvider";
import { useFetchActions, ACTION_TYPE_SLUG } from "@core/features/actions";
import { usePlatformStore } from "@platform/store";
import { MeetingDetails } from "../types/video.types";

const VideoMeetingPage = () => {
  const { priorityBasedPolicy } = useAppState();

  const { sharingAttendeeId } = useContentShareState();

  const user = usePlatformStore((s) => s.user);

  useMeetingEndRedirect();

  const {
    meetingManager,
    isInitialized,
    error,
    meetingDetails,
    fetchMeetingDetails,
    checkMeetingDetailsAndRedirect,
  } = useCustomMeetingManager();

  const navigate = useNavigate();

  // Extract meeting ID from URL params
  const { meetingId: meetingIdFromUrl } = useParams();

  // Ensure Chime SDK device lists are refreshed when our custom device provider detects new devices
  useDeviceRefresh();

  const { data: actionsData } = useFetchActions({
    programId: meetingDetails?.program ?? 0,
    user,
    isEnabled: !!user && !!meetingDetails?.program,
  });

  useEffect(() => {
    // Check for meeting ID in URL and redirect to join page if needed
    if (meetingIdFromUrl) {
      if (!meetingDetails) {
        fetchMeetingDetails(meetingIdFromUrl);
      } else {
        // We have meeting details, check if we should redirect to join page
        checkMeetingDetailsAndRedirect(meetingIdFromUrl);
      }
    }
  }, [
    meetingIdFromUrl,
    meetingDetails,
    fetchMeetingDetails,
    checkMeetingDetailsAndRedirect,
  ]);

  // Find meeting script action for this meeting
  const meetingScriptAction = useMemo(
    () =>
      actionsData?.actions
        ?.filter(
          (action) =>
            action.action_type?.slug === ACTION_TYPE_SLUG.MEETING_SCRIPT
        )
        .find(
          (action) =>
            action.binding_object_id === parseInt(meetingIdFromUrl || "0", 10)
        ),
    [actionsData?.actions, meetingIdFromUrl]
  );

  // If we have a meeting ID but no meeting manager, redirect to join page
  useEffect(() => {
    if (meetingDetails && !isInitialized && meetingIdFromUrl) {
      navigate(`/video/join/${meetingIdFromUrl}`);
    }
  }, [meetingDetails, isInitialized, meetingIdFromUrl, navigate]);

  // If we have a meeting ID but no meeting manager, redirect to join page
  useEffect(() => {
    if (!isInitialized || !meetingManager) {
      if (meetingIdFromUrl) {
        navigate(`/video/join/${meetingIdFromUrl}`);
      }
    }
  }, [isInitialized, meetingManager, meetingIdFromUrl, navigate]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (!isInitialized || !meetingManager) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Please join the meeting from the join page.</p>
      </div>
    );
  }

  return (
    <VideoUIProvider>
      <NotificationProvider>
        <UserActivityProvider>
          <DataMessagesProvider>
            <DevicePermissionInitializer />
            <MeetingStatusNotifier />
            <VideoUIFrameContainer
              priorityBasedPolicy={!!priorityBasedPolicy}
              sharingAttendeeId={sharingAttendeeId || ""}
              meetingScriptAction={meetingScriptAction}
              meetingDetails={meetingDetails || ({} as MeetingDetails)}
            />
          </DataMessagesProvider>
        </UserActivityProvider>
      </NotificationProvider>
    </VideoUIProvider>
  );
};

export default VideoMeetingPage;
